import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { action, makeAutoObservable } from 'mobx';
import * as API from '@/API';
import UserGroupModel from '@/Models/PermissionsConfiguration/UserGroupModel';
import MenuItemPermissionsModel from '@/Models/PermissionsConfiguration/MenuItemPermissionsModel';
import { isNullOrWhiteSpace } from '@/Utility/StringFunctions';
import RoleModel from '@/Models/PermissionsConfiguration/RoleModel';
import PermissionModel from '@/Models/PermissionsConfiguration/PermissionModel';
import { GroupModel } from '@/Models/PermissionsConfiguration/GroupModel';
import { AddUserModel, AddUserModelErrors, safeParse } from '@/Models/PermissionsConfiguration/AddUserModel';
import { t } from 'i18next';

export type PermissionsConfigurationTab = 'users' | 'groups' | 'roles';

export interface StateData {
    currentTab: PermissionsConfigurationTab;
    users: UserGroupModel[];
    groups: GroupModel[];
    menuItems: MenuItemPermissionsModel[];
    permissions: PermissionModel[];
    roles: RoleModel[];
    showInactiveUsers: boolean;
    selectedGroupId: number;
    userFilter: string;
    assignedUserFilter: string;
    selectedRole: number;
}

export interface IPermissionsConfigurationStore {
    setCurrentTab(tab: PermissionsConfigurationTab): void;
    setShowInactiveUsers(showInactiveUsers: boolean): void;
    setGroupOption(userId: number, groupId: number): void;
    setUserActive(userId: number, active: boolean): void;
    applyUserChanges(user: UserGroupModel): void;
    setSelectedGroup(groupId: number): void;
    goToGroup(groupId: number): void;
    get allUsers(): UserGroupModel[];
    get usersInGroup(): { Id: number; Name: string }[];
    setCreateUserDialogOpen(open: boolean): void;
    setSelectedRole(Id: number): void;
    get selectedRole(): RoleModel | undefined;
    get selectedGroup(): GroupModel | undefined;
    get templateUsers(): UserGroupModel[];
    setUserFilter(filter: string): void;
    setAssignedUserFilter(filter: string): void;
    toggleEnabledMenuItem(menuItemId: number): void;
    isMenuItemEnabled(id: number): boolean;
    toggleRolePermission(permissionId: number, checked: boolean): void;
    toggleGroupRole(roleId: number, checked: boolean): void;
    setCreateGroupDialogOpen(val: boolean): void;
    setNewGroupName(val: string): void;
    createNewGroup(groupName?: string): void;
    setDeleteCurrentGroupDialogOpen(val: boolean): void;
    deleteSelectedGroup(): void;
    setNewUserUsername(val: string): void;
    setNewUserName(val: string): void;
    setNewUserPassword(val: string): void;
    setNewUserRepeatPassword(val: string): void;
    setNewUserGroup(val: number): void;
    setNewUserTemplate(val: number): void;
    createUser(): void;
    setCreateNewRoleDialogOpen(val: boolean): void;
    setDeleteCurrentRoleDialogOpen(val: boolean): void;
    setNewRoleName(val: string): void;
    createNewRole(): void;
    deleteSelectedRole(): void;
    setTitle(title: string): void;
}

export default class PermissionsConfigurationStore implements IPermissionsConfigurationStore {
    state: StateData = {
        currentTab: 'users',
        users: [],
        groups: [],
        menuItems: [],
        permissions: [],
        roles: [],
        showInactiveUsers: false,
        selectedGroupId: 0,
        userFilter: '',
        assignedUserFilter: '',
        selectedRole: 0,
    };

    createUserDialogOpen: boolean;
    newUserData: AddUserModel;
    newUserErrors: AddUserModelErrors | null;
    newUserServerError: string | null;

    createGroupDialogOpen: boolean;
    newGroupName: string;

    deleteCurrentGroupDialogOpen: boolean;
    deleteError: string | null;

    createNewRoleDialogOpen: boolean;
    newRoleName: string;

    deleteCurrentRoleDialogOpen: boolean;
    roleDeleteError: string | null;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;

        this.createUserDialogOpen = false;
        this.newUserData = {} as AddUserModel;
        this.newUserErrors = null;
        this.newUserServerError = null;

        this.createGroupDialogOpen = false;
        this.newGroupName = '';

        this.deleteCurrentGroupDialogOpen = false;
        this.deleteError = null;

        this.createNewRoleDialogOpen = false;
        this.newRoleName = '';

        this.deleteCurrentRoleDialogOpen = false;
        this.roleDeleteError = null;

        makeAutoObservable<PermissionsConfigurationStore, 'initNew'>(this, {
            initNew: action,
        });

        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    setCurrentTab(tab: PermissionsConfigurationTab): void {
        this.setState({ ...this.state, currentTab: tab });
    }

    setShowInactiveUsers(showInactiveUsers: boolean): void {
        this.setState({ ...this.state, showInactiveUsers: showInactiveUsers });
    }

    setGroupOption(userId: number, groupId: number): void {
        const user = this.state.users.find((x) => x.Id == userId);
        if (user == null) {
            return;
        }
        user.GroupId = groupId;
        this.applyUserChanges(user);
        this.fcStore.updateState(this.state);
    }

    setUserActive(userId: number, active: boolean): void {
        const user = this.state.users.find((x) => x.Id == userId);
        if (user == null) {
            return;
        }
        user.Active = active;
        this.applyUserChanges(user);
        this.fcStore.updateState(this.state);
    }

    applyUserChanges(user: UserGroupModel): void {
        void API.permissions.saveUser(user)
            .catch((error) => {
                console.error('Could not save', error);
            });
    }

    setSelectedGroup(groupId: number): void {
        this.setState({ ...this.state, selectedGroupId: groupId });
    }

    goToGroup(groupId: number): void {
        this.setSelectedGroup(groupId);
        this.setCurrentTab('groups');
    }

    get allUsers(): UserGroupModel[] {
        return this.state.users
            .filter((u) => (this.state.showInactiveUsers || u.Active)
                && (isNullOrWhiteSpace(this.state.userFilter) || u.Name.toLowerCase().includes(this.state.userFilter)));
    }

    get usersInGroup(): { Id: number; Name: string }[] {
        return this.state.users
            .filter((x) =>
                x.GroupId == this.state.selectedGroupId
                && (isNullOrWhiteSpace(this.state.assignedUserFilter) || x.Name.toLowerCase().includes(this.state.assignedUserFilter)),
            )
            .sort((a, b) => a.Name.localeCompare(b.Name))
            .map((x) => ({ Id: x.Id, Name: x.Name }));
    }

    setCreateUserDialogOpen(open: boolean): void {
        this.createUserDialogOpen = open;
    }

    setSelectedRole(Id: number): void {
        this.setState({ ...this.state, selectedRole: Id });
    }

    get selectedRole(): RoleModel | undefined {
        return this.state.roles.find((x) => x.Id == this.state.selectedRole);
    }

    get selectedGroup(): GroupModel | undefined {
        return this.state.groups.find((x) => x.Id == this.state.selectedGroupId);
    }

    get templateUsers(): UserGroupModel[] {
        return this.state.users.filter((x) => x.Username.startsWith('template'));
    }

    setUserFilter(filter: string): void {
        this.setState({ ...this.state, userFilter: filter });
    }

    setAssignedUserFilter(filter: string): void {
        this.setState({ ...this.state, assignedUserFilter: filter });
    }

    toggleEnabledMenuItem(menuItemId: number): void {
        if (!this.selectedRole) {
            return;
        }

        // In case we clicked one of the parent MenuItems
        const parent = this.state.menuItems.find((x) => x.Id == menuItemId);
        const isParent = parent?.ParentId == null && this.state.menuItems.some((x) => x.ParentId == menuItemId);
        if (parent && isParent) {
            const parentChecked = this.isMenuItemEnabled(menuItemId);
            const children = this.state.menuItems.filter((x) => x.ParentId == menuItemId).map((x) => x.Id);
            if (parentChecked) {
                // Uncheck checked children only
                children.forEach((childId) => {
                    if (this.selectedRole!.EnabledMenuItemIds.includes(childId)) {
                        this.selectedRole!.EnabledMenuItemIds = this.selectedRole!.EnabledMenuItemIds.filter((x) => x != childId);
                        void API.permissions.toggleRoleMenuItem(this.state.selectedRole, childId);
                    }
                });
            }
            else {
                // Check unchecked children only
                children.forEach((childId) => {
                    if (!this.selectedRole!.EnabledMenuItemIds.includes(childId)) {
                        this.selectedRole!.EnabledMenuItemIds.push(childId);
                        void API.permissions.toggleRoleMenuItem(this.state.selectedRole, childId);
                    }
                });
            }
            this.fcStore.updateState(this.state);
            return;
        }

        const found = this.selectedRole.EnabledMenuItemIds.find((x) => x == menuItemId) ?? false;
        if (!found) {
            this.selectedRole.EnabledMenuItemIds.push(menuItemId);
        }
        else {
            this.selectedRole.EnabledMenuItemIds = this.selectedRole.EnabledMenuItemIds.filter((x) => x != menuItemId);
        }

        void API.permissions.toggleRoleMenuItem(this.state.selectedRole, menuItemId);
        this.fcStore.updateState(this.state);
    }

    isMenuItemEnabled(id: number): boolean {
        if (this.state.selectedRole == 0) {
            return false;
        }

        if (this.selectedRole?.EnabledMenuItemIds.includes(id)) {
            return true;
        }

        const parent = this.state.menuItems.find((x) => x.Id == id && x.ParentId == null);
        if (parent == null) {
            return false;
        }

        const allChildren = this.state.menuItems.filter((x) => x.ParentId == parent.Id);
        if (allChildren.length == 0) {
            return this.selectedRole?.EnabledMenuItemIds.includes(id) ?? false;
        }

        return allChildren.every((x) => this.selectedRole?.EnabledMenuItemIds.includes(x.Id));
    }

    toggleRolePermission(permissionId: number, checked: boolean): void {
        void API.permissions.toggleRolePermission(this.state.selectedRole, permissionId)
            .then(() => {
                const role = this.state.roles.find((x) => x.Id == this.state.selectedRole);
                if (role == null) {
                    return;
                }
                if (checked) {
                    role.EnabledPermissionIds.push(permissionId);
                }
                else {
                    role.EnabledPermissionIds = role.EnabledPermissionIds.filter((x) => x != permissionId);
                }
                this.fcStore.updateState(this.state);
            });
    }

    toggleGroupRole(roleId: number, checked: boolean): void {
        void API.permissions.toggleGroupRole(this.state.selectedGroupId, roleId)
            .then(() => {
                const group = this.state.groups.find((x) => x.Id == this.state.selectedGroupId);
                if (group == null) {
                    return;
                }
                if (checked) {
                    group.Roles.push(roleId);
                }
                else {
                    group.Roles = group.Roles.filter((x) => x != roleId);
                }
                this.fcStore.updateState(this.state);
            });
    }

    goToRole(roleId: number): void {
        this.setState({ ...this.state, currentTab: 'roles', selectedRole: roleId });
    }

    setCreateGroupDialogOpen(val: boolean): void {
        this.createGroupDialogOpen = val;
    }

    setNewGroupName(val: string): void {
        this.newGroupName = val;
    }

    createNewGroup(groupName?: string) {
        if (groupName != undefined) {
            this.newGroupName = groupName;
        }

        void API.permissions.createNewGroup(this.newGroupName)
            .then((groups) => {
                this.setState({ ...this.state, groups: groups });
                this.setCreateGroupDialogOpen(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    setDeleteCurrentGroupDialogOpen(val: boolean) {
        this.deleteCurrentGroupDialogOpen = val;
        this.deleteError = null;
    }

    deleteSelectedGroup() {
        void API.permissions.deleteGroup<GroupModel[], { Message: string }>(this.state.selectedGroupId,
            (groups) => {
                this.state.groups = groups;
                this.setDeleteCurrentGroupDialogOpen(false);
                this.setSelectedGroup(groups[0].Id);
                this.fcStore.updateState(this.state);
            },
            (err: { Message: string }) => {
                this.deleteError = err.Message;
            },
        );
    }

    setNewUserUsername(val: string) {
        this.newUserData.username = val;
    }

    setNewUserName(val: string) {
        this.newUserData.name = val;
    }

    setNewUserPassword(val: string) {
        this.newUserData.password = val;
    }

    setNewUserRepeatPassword(val: string) {
        this.newUserData.repeatPassword = val;
    }

    setNewUserGroup(val: number) {
        this.newUserData.group = val;
    }

    setNewUserTemplate(val: number) {
        this.newUserData.templateUser = val;
    }

    createUser() {
        this.newUserErrors = null;
        const parseResult = safeParse(this.newUserData);
        if (parseResult.errors) {
            this.newUserErrors = parseResult.errors;
            return;
        }
        void API.permissions.createUser(this.newUserData)
            .then((res) => {
                if (!res) {
                    return;
                }
                if (res.AllOk) {
                    this.newUserData = {} as AddUserModel;
                    this.setCreateUserDialogOpen(false);

                    void API.permissions.getAllUsers()
                        .then((users) => this.setState({ ...this.state, users }));
                }
                else {
                    console.error('An error occured while creating the new user');
                    const errors: string[] = [];
                    if (res.ErrorFullUserNameText) {
                        errors.push(res.ErrorFullUserNameText);
                    }
                    if (res.ErrorGroupText) {
                        errors.push(res.ErrorGroupText);
                    }
                    if (res.ErrorPasswordText) {
                        errors.push(res.ErrorPasswordText);
                    }
                    if (res.ErrorPassword2Text) {
                        errors.push(res.ErrorPassword2Text);
                    }
                    if (res.ErrorUserNameText) {
                        errors.push(res.ErrorUserNameText);
                    }
                    if (res.ErrorText) {
                        errors.push(res.ErrorText);
                    }
                    if (errors.length) {
                        this.newUserServerError = errors.join('<br />');
                    }
                }
            });
    }

    setCreateNewRoleDialogOpen(val: boolean) {
        this.createNewRoleDialogOpen = val;
    }

    setDeleteCurrentRoleDialogOpen(val: boolean) {
        this.deleteCurrentRoleDialogOpen = val;
        this.roleDeleteError = null;
    }

    setNewRoleName(val: string) {
        this.newRoleName = val;
    }

    createNewRole() {
        if (this.newRoleName.trim() == '') {
            return;
        }
        void API.permissions.createRole(this.newRoleName)
            .then((roles) => {
                this.setState({ ...this.state, roles: roles });
            });
    }

    deleteSelectedRole() {
        if (!this.state.selectedRole) {
            return;
        }

        void API.permissions.deleteRole<RoleModel[], { Message: string }>(
            this.state.selectedRole,
            (roles) => {
                this.setState({ ...this.state, roles: roles, selectedRole: roles[0]?.Id ?? 0 });
                this.setDeleteCurrentRoleDialogOpen(false);
            },
            (err: { Message: string }) => {
                this.roleDeleteError = err.Message;
            },
        );
    }

    private readonly initNew = (): void => {
        void Promise.all([
            API.permissions.getAllUsers(),
            API.permissions.getGroups(),
            API.permissions.getMenuItems(),
            API.permissions.getRoles(),
            API.permissions.getPermissions(),
        ])
            .then(([users, groups, menuItems, roles, permissions]) => {
                this.setState({
                    ...this.state,
                    users: users,
                    groups: groups,
                    menuItems: menuItems,
                    roles: roles,
                    permissions: permissions,
                });
                this.setTitle(t('UserManagement'));
            });
    };

    private readonly initOld = (state: StateData): void => {
        this.setState(state);
        this.setTitle(t('UserManagement'));
    };

    setTitle(title: string): void {
        this.fcStore.updateTitle(title);
    }

    private readonly setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };
}
